import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext
} from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";

import { useApolloClient, useQuery } from "react-apollo";

import gql from "graphql-tag";

import { Field, Control, Label, Radio, Button, Icon } from "rbx";

import { generateOrderDispatchingColumns } from "./columns";

import { ReactTable } from "../../../components/DataTable";

import { useCurrentUser, useSystemCodes } from "../../../utils/hooks";

import { useOrdersByTripKey } from "../../../hooks";
import TripList from "../Trips/TripList";

import {
  groupBy,
  uuidGenerator,
  ourToast as toast,
  deepFlatten
} from "../../../utils";

import {
  UPDATE_ORDER_MUTATION,
  REFRESH_ORDER_MUTATION
} from "../../../graphql/Orders/Orders.graphql";

import { ALL_BOOKING_CODES_QUERY } from "../../../graphql/BookingCodes/BookingCodes.graphql";

import { ModalContext } from "../../../components/ModalProvider";

import { AddBookingForm } from "../../../components/Bookings";

import { Confirmation } from "../../../components/modals";

import { ALL_DIRECTIONS_QUERY } from "../../../graphql/Directions/Directions.graphql";
import { printDirections } from "../../../utils/export";

const GET_ORDER_NUMBERS = gql`
  query Orders($where: OrderWhereInput) {
    Orders(
      where: $where
      orderByMulti: [
        PickupDriverRecIdRef_ASC
        PickupLoc_SI_ASC
        PickupDate_ASC
        OrderNumber_ASC
      ]
    ) {
      RecId
      OrderNumber
    }
  }
`;

const ALL_ORDERS_QUERY = gql`
  query Orders($where: OrderWhereInput) {
    Orders(where: $where, orderByMulti: [PickupDate_DESC]) {
      RecId
      StartDate
      PickupDateTime
      PickupDate
      # PickupTime
      TripKey
      Trip
      Leg
      OrderNumber
      EstimatedLoad
      BayNumber
      StatusRecIdRef
      BookingCodeRecIdRef
      BookingRecIdRef
      Booking {
        RecId
        PickupDate
        BookingDate
        Comment
        OperatorRecIdRef
      }
      Status {
        RecId
        CodeDescription
        CodeId
      }
      PickupLocation {
        RecId
        City
        State
      }
      DeliveryLocation {
        RecId
        City
        State
      }
      PickupDriverRecIdRef
      PickupDriver {
        RecId
        SeniorityNo
        FullName
        NextCertExp
      }
      PickupTractorRecIdRef
      PickupTractor {
        RecId
        VehicleNumber
      }
      PickupTrailerRecIdRef
      PickupTrailer {
        RecId
        VehicleNumber
      }
      Route {
        RecId
        Contract
        LoadTime
        ReturnToTerminalTime
        TravelTime
        UnloadTime
        BookingCode {
          RecId
          Color
          BookingCode
          Description
        }
        StartLocation {
          RecId
          LocationID
          LocationName
          City
          State
        }
        PickupLocation {
          RecId
          LocationID
          LocationName
          City
          State
        }
        EndingLocation {
          RecId
          LocationID
          LocationName
          City
          State
        }
        DeliveryLocation {
          RecId
          LocationID
          LocationName
          City
          State
        }
      }
    }
  }
`;

const transformOrders = orders => {
  // GROUP BY TRIP NUMBER AND PICK UP DATE
  const trips = groupBy(orders, x => {
    const [date, , trip] = x.TripKey.split("_");
    return `${trip}_${date}`;
  });
  const getBookedTrip = (
    bkCode = "",
    startDate = "",
    pickupCity = "",
    pickupState = "",
    delCity = "",
    delState = ""
  ) =>
    `${bkCode ||
      ""}${startDate} ${pickupCity}, ${pickupState} > ${delCity}, ${delState}`;

  const getDescription = (
    os // orders
  ) =>
    os
      .map(
        ({ PickupLocation, DeliveryLocation }) =>
          `${PickupLocation.City}, ${PickupLocation.State} to ${DeliveryLocation.City}, ${DeliveryLocation.State}`
      )
      .join(" / ");

  const result = Object.keys(trips).map(x => {
    trips[x] = trips[x].sort((a, b) => {
      if (a.Leg > b.Leg) return 1;
      if (b.Leg > a.Leg) return -1;
      return 0;
    });

    const getBookedTrips = xs =>
      xs.map(order =>
        getBookedTrip(
          "",
          moment(order.StartDate).format("HH:mm, MM/DD/YY"),
          order.PickupLocation.City,
          order.PickupLocation.State,
          order.DeliveryLocation.City,
          order.DeliveryLocation.State
        )
      );

    const BookedTrip = getBookedTrips(trips[x]).join(" / ");

    const Description = getDescription(trips[x]);

    return {
      id: uuidGenerator(),
      Trip: x.split(/_/)[0], // GET RID OF PICK UP DATE
      BookedTrip,
      Description,
      Booking: trips[x][0].Booking,
      Status: trips[x][0].Status,
      StartDate: trips[x][0].StartDate,
      PickupCity: trips[x][0].PickupLocation.City,
      PickupState: trips[x][0].PickupLocation.State,
      DeliveryCity: trips[x][0].DeliveryLocation.City,
      DeliveryState: trips[x][0].DeliveryLocation.State,
      PickupDriverRecIdRef: trips[x][0].PickupDriverRecIdRef,
      PickupTrailerRecIdRef: trips[x][0].PickupTrailerRecIdRef,
      PickupTractorRecIdRef: trips[x][0].PickupTractorRecIdRef,
      PickupDriver: trips[x][0].PickupDriver,
      BookingCodeRecIdRef: trips[x][0].BookingCodeRecIdRef,
      BookingRecIdRef: trips[x][0].BookingRecIdRef,
      Orders: trips[x]
    };
  });
  return result;
};

const OrderDispatchingGrid = ({ date, selectedDriver }) => {
  const { REACT_APP_REPORT_BASE_URL = "https://gdev.gravoc.com" } = process.env;
  const BASE_URL = `${REACT_APP_REPORT_BASE_URL}/ReportViewer/ReportViewer.aspx?Path=Daily%20Schedule`;
  const REPORT_URL = `${BASE_URL}&Param=Startdate&ParamValue=${moment(
    date
  ).format("MM/DD/YYYY")}`;
  const client = useApolloClient();
  const { setModalOpen } = useContext(ModalContext);

  const [printDirectionsLoading, setPrintDirectionsLoading] = useState(false);

  const OrdersSystemCodes = useSystemCodes({
    Category_in: ["BookingCodes", "Order", "ELD"],
    CodeName_in: ["BookCatg", "OrderStatus", "ELDStatus"],
    CodeId_in: ["O", "BK-E", "EXP", "EX", "SCH"]
  });

  const VehicleAvailableSystemCode = useSystemCodes({
    Category: "Fleet",
    CodeName: "VehicleStatus",
    CodeId: "A"
  });

  const { data: BookingChangeData } = useQuery(ALL_BOOKING_CODES_QUERY, {
    variables: {
      where: {
        BookingCode: "BC01"
      }
    }
  });

  const OrderScheduledStatusRecIdRef = useMemo(
    () =>
      OrdersSystemCodes?.data?.find(status => status.CodeId === "SCH")?.RecId,
    [OrdersSystemCodes]
  );

  const bookingChangeCode = useMemo(() => {
    const bookingCodes = BookingChangeData?.BookingCodes;
    if (Array.isArray(bookingCodes) && bookingCodes[0]) {
      return bookingCodes[0];
    }
    return {};
  }, [BookingChangeData]);

  const { data: currentUser } = useCurrentUser();

  const [showAll, setShowAll] = useState(false);

  const [data, setData] = useState([]);

  const [selected, setSelected] = useState([]);

  const [, setLoading] = useState(false);

  const [tripKeys, setTripKeys] = useState([]);

  const { orders } = useOrdersByTripKey(tripKeys);

  const { data: OrdersData, refetch } = useQuery(ALL_ORDERS_QUERY, {
    query: ALL_ORDERS_QUERY,
    variables: {
      where: {
        StartDate_gte: moment(date)
          .startOf("day")
          .toISOString(),
        StartDate_lt: moment(date)
          .endOf("day")
          .toISOString()
      }
    },
    fetchPolicy: "network-only"
  });

  useEffect(() => {
    if (OrdersData?.Orders) {
      setTripKeys(OrdersData.Orders.map(o => o.TripKey));
    }
  }, [OrdersData]);

  useEffect(() => {
    if (orders) {
      if (!showAll && selectedDriver) {
        const filteredOrders = orders.filter(
          o =>
            !o.PickupDriver ||
            parseInt(o.PickupDriver.SeniorityNo, 10) >=
              parseInt(selectedDriver.SeniorityNo, 10)
        );
        setData(transformOrders(filteredOrders));
      } else {
        setData(transformOrders(orders));
      }
    }
  }, [orders, selectedDriver, showAll]);

  const handleSelectAll = useCallback((allSelected, orderNumbers) => {
    setSelected(prev => {
      const curr = [...prev];
      for (const orderNumber of orderNumbers) {
        const idx = curr.findIndex(item => item === orderNumber);
        if (allSelected) {
          if (idx > -1) {
            curr.splice(idx, 1);
          }
        } else if (idx < 0) {
          curr.push(orderNumber);
        }
      }
      return curr;
    });
  }, []);

  const handleSelect = useCallback(trip => {
    setSelected(prev => {
      const curr = [...prev];
      for (const order of trip.Orders) {
        const idx = curr.findIndex(item => item === order.OrderNumber);
        if (idx > -1) {
          curr.splice(idx, 1);
        } else {
          curr.push(order.OrderNumber);
        }
      }

      return curr;
    });
  }, []);

  const ORDER_DISPATCHING_COLUMNS = useMemo(() => {
    const updateOrders = async (updateData, orderRecIds) => {
      try {
        await Promise.all(
          orderRecIds.map(orderRecId =>
            client.mutate({
              mutation: UPDATE_ORDER_MUTATION,
              variables: {
                data: updateData,
                where: {
                  RecId: orderRecId
                }
              }
            })
          )
        );
        // fetchData();
        refetch();
      } catch (err) {
        console.log(err.message);
      }
    };

    const handleComplete = () => {
      setModalOpen(false, "");
      // fetchData();
      refetch();
    };

    const handleChange = (index, id, name, value, update = true) => {
      const currData = [...data];

      if (Array.isArray(name)) {
        for (let i = 0; i < name.length; i += 1) {
          currData[index][name[i]] = value;
        }
      } else {
        currData[index][name] = value;
      }

      setData(currData);

      if (
        Array.isArray(name) &&
        ["PickupDriverRecIdRef", "DeliveryDriverRecIdRef"].every(key =>
          name.includes(key)
        )
      ) {
        setModalOpen(
          true,
          <AddBookingForm
            state={{
              EmployeeRecIdRef: parseInt(value, 10),
              OperatorRecIdRef: currentUser.RecId,
              BookingStartDate: date,
              BookingCodeRecIdRef: data[index].BookingCodeRecIdRef,
              BookedTrip: data[index].BookedTrip,
              PickupDate: date
            }}
            onComplete={({ RecId } = {}) => {
              if (RecId) {
                handleChange(index, id, "BookingRecIdRef", RecId);
              }
              handleComplete();
            }}
          />
        );
      }
      if (update) {
        const updates = {};
        if (Array.isArray(name)) {
          name.forEach(n => (updates[n] = parseInt(value)));
        } else {
          updates[name] = parseInt(value);
        }
        updateOrders(
          updates,
          currData[index].Orders.map(x => x.RecId)
        );
      }
      return currData[index].Orders.map(x => x.RecId);
    };

    const handleAssignDriver = (rowIndex, columnId) => {
      if (!data[rowIndex].PickupDriverRecIdRef) {
        if (selectedDriver) {
          setModalOpen(
            true,
            <AddBookingForm
              state={{
                EmployeeRecIdRef: selectedDriver.RecId,
                OperatorRecIdRef: currentUser.RecId,
                BookingStartDate: date,
                BookingCodeRecIdRef: data[rowIndex].BookingCodeRecIdRef,
                BookedTrip: data[rowIndex].BookedTrip,
                PickupDate: date
              }}
              onComplete={(update = true) => {
                if (update) {
                  handleChange(
                    rowIndex,
                    columnId,
                    ["PickupDriverRecIdRef", "DeliveryDriverRecIdRef"],
                    selectedDriver.RecId
                  );
                }
                handleComplete();
              }}
            />
          );
        }
      } else {
        window.alert(
          "Please remove current driver from trip before assigning."
        );
      }
    };
    const handleRemoveDriver = async (index, id, value) => {
      const reallyRemoveDriver = () => {
        const orders2 = handleChange(
          index,
          id,
          ["PickupDriverRecIdRef", "DeliveryDriverRecIdRef"],
          null,
          false
        );
        const orders3 = handleChange(index, id, "BookingRecIdRef", null, false);
        const ordersToUpdate = [...new Set([...orders2, ...orders3])];
        updateOrders(
          {
            PickupDriverRecIdRef: null,
            DeliveryDriverRecIdRef: null,
            BookingRecIdRef: null,
            StatusRecIdRef: OrderScheduledStatusRecIdRef
          },
          ordersToUpdate
        );

        setModalOpen(
          true,
          <AddBookingForm
            state={{
              EmployeeRecIdRef: value,
              OperatorRecIdRef: currentUser.RecId,
              BookingStartDate: date,
              PickupDate: date,
              BookingCodeRecIdRef: bookingChangeCode.RecId || null
            }}
            onComplete={handleComplete}
          />
        );
      };
      setModalOpen(
        true,
        <Confirmation
          message="Are you sure you want to remove this driver?"
          onConfirm={reallyRemoveDriver}
        />
      );
    };
    const handleExport = async ordersToExport => {
      try {
        if (!ordersToExport.length) {
          throw Error("Please select some orders to be exported");
        }
        setLoading(true);
        const ELDStatus = OrdersSystemCodes.data.find(s => s.CodeId === "EX");
        const Status = OrdersSystemCodes.data.find(s => s.CodeId === "EXP");
        await Promise.all(
          ordersToExport.map(({ RecId }) =>
            client.mutate({
              mutation: UPDATE_ORDER_MUTATION,
              variables: {
                data: {
                  StatusRecIdRef: Status.RecId,
                  ELDStatusRecIdRef: ELDStatus.RecId,
                  DateExported: new Date()
                },
                where: {
                  RecId
                }
              }
            })
          )
        );
        setLoading(false);
        toast.success("Orders exported successfully");
        // fetchData();
        refetch();
      } catch (err) {
        toast.error("Error exporting Orders.");
      }
    };

    const handleResetToBooked = async ordersToReset => {
      try {
        if (!ordersToReset.length) {
          throw Error("Please select some orders to be reset to booked");
        }
        setLoading(true);
        const Status = OrdersSystemCodes.data.find(s => s.CodeId === "BK-E");
        await Promise.all(
          ordersToReset.map(({ RecId }) =>
            client.mutate({
              mutation: UPDATE_ORDER_MUTATION,
              variables: {
                data: {
                  StatusRecIdRef: Status.RecId,
                  ELDStatusRecIdRef: null
                },
                where: {
                  RecId
                }
              }
            })
          )
        );
        setLoading(false);
        toast.success("Orders reseted to booked successfully");
        // fetchData();
        refetch();
      } catch (err) {
        toast.error("Error reseting Orders to Booked.");
      }
    };

    if (
      OrdersSystemCodes.data &&
      Array.isArray(OrdersSystemCodes.data) &&
      OrdersSystemCodes.data.length >= 1 &&
      VehicleAvailableSystemCode.data &&
      Array.isArray(VehicleAvailableSystemCode.data) &&
      VehicleAvailableSystemCode.data.length >= 1
    ) {
      return generateOrderDispatchingColumns({
        selected,
        onSelect: handleSelect,
        onSelectAll: handleSelectAll,
        onChange: handleChange,
        currentUser,
        fetchData: refetch,
        handleAssignDriver,
        handleRemoveDriver,
        VehicleAvailableSystemCode: VehicleAvailableSystemCode.data[0],
        OrdersSystemCodes: OrdersSystemCodes?.data,
        handleExport,
        handleResetToBooked
      });
    }
  }, [
    OrdersSystemCodes.data,
    VehicleAvailableSystemCode.data,
    refetch,
    client,
    setModalOpen,
    data,
    currentUser,
    date,
    selectedDriver,
    OrderScheduledStatusRecIdRef,
    bookingChangeCode.RecId,
    selected,
    handleSelect,
    handleSelectAll
  ]);

  const [printBOLsLoading, setPrintBOLsLoading] = useState(false);

  const handlePrintBillOfLadings = async () => {
    try {
      setPrintBOLsLoading(true);
      const { data: GetOrdersData } = await client.query({
        query: GET_ORDER_NUMBERS,
        variables: {
          where: {
            OrderNumber_in: selected
          }
        }
      });
      if (GetOrdersData?.Orders) {
        await Promise.all(
          GetOrdersData.Orders.map(o =>
            client.mutate({
              mutation: REFRESH_ORDER_MUTATION,
              variables: {
                where: {
                  RecId: o.RecId
                }
              }
            })
          )
        );
      }
      window.open(
        `${REACT_APP_REPORT_BASE_URL}/ReportViewer/ReportViewer.aspx?Path=BOL&Param=BOLNumber&ParamValue=${selected.join(
          ","
        )}`
      );
    } catch (err) {
      console.error(err.message);
    } finally {
      setPrintBOLsLoading(false);
    }
  };

  const handlePrintDirections = async () => {
    try {
      setPrintDirectionsLoading(true);
      const trips = selected.reduce((acc, curr) => {
        const trip = data.find(x =>
          x.Orders.some(order => order.OrderNumber === curr)
        );
        if (trip && !acc.some(x => x.id === trip.id)) {
          acc.push(trip);
        }
        return acc;
      }, []);

      const runs = trips.map(trip => {
        const legZeroStartLocation = trip.Orders[0].Route.StartLocation.RecId;
        const legZeroEndLocation = trip.Orders[0].Route.PickupLocation.RecId;

        const legs = trip.Orders.reduce((acc, order, idx, arr) => {
          const startLocation = order.Route.PickupLocation.RecId;
          const endLocation = order.Route.DeliveryLocation.RecId;
          const result = [[startLocation, endLocation]];

          if (idx > 0) {
            const prevLegEndLocation =
              arr[idx - 1].Route.DeliveryLocation.RecId;
            const currLegStartLocation = order.Route.PickupLocation.RecId;
            result.unshift([prevLegEndLocation, currLegStartLocation]);
          }
          return [...acc, ...result];
        }, []);

        const lastLegStartLocation = trip.Orders.at(-1).Route.DeliveryLocation
          .RecId;
        const lastLegEndLocation = trip.Orders.at(-1).Route.EndingLocation
          .RecId;

        return {
          legs: [
            [legZeroStartLocation, legZeroEndLocation],
            ...legs,
            [lastLegStartLocation, lastLegEndLocation]
          ]
        };
      });

      const directions = await Promise.all(
        runs.map(async trip =>
          Promise.all(
            trip.legs.map(async leg => {
              const { data: DirectionData } = await client.query({
                query: ALL_DIRECTIONS_QUERY,
                variables: {
                  where: {
                    OriginLocationRecIdRef: leg[0],
                    DestinationLocationRecIdRef: leg[1],
                    Priority_not: null
                  },
                  orderByMulti: ["Priority_ASC"]
                }
              });
              return DirectionData?.Directions;
            })
          )
        )
      );
      for (const direction of directions) {
        printDirections(...deepFlatten(direction));
      }
    } catch (err) {
      console.log(err.message);
    } finally {
      setPrintDirectionsLoading(false);
    }
  };
  const handleShowAllToggle = () => setShowAll(!showAll);

  // if (OrdersSystemCodes.loading || loading) return <Loading />;

  return (
    <div>
      <div className="order-dispatching-header">
        <TripList tripKeys={[date]}>
          <Field kind="group">
            <Control>
              <Label>
                <Radio name="available" onChange={handleShowAllToggle} />
                &nbsp;All
              </Label>
              <Label>
                <Radio
                  defaultChecked
                  name="available"
                  onChange={handleShowAllToggle}
                />
                &nbsp;Available
              </Label>
            </Control>
            <Control>
              <Button
                color="link"
                size="small"
                onClick={() => window.open(REPORT_URL)}
              >
                <Icon>
                  <FontAwesomeIcon icon="print" />
                </Icon>
                <span>Driver Schedule</span>
              </Button>
            </Control>
            <Control>
              <Button
                color="link"
                disabled={!selected.length || printBOLsLoading}
                size="small"
                state={printBOLsLoading ? "loading" : ""}
                onClick={handlePrintBillOfLadings}
              >
                <Icon>
                  <FontAwesomeIcon icon="print" />
                </Icon>
                <span>{`Print ${selected.length || ""} BOL${
                  selected.length > 1 ? "s" : ""
                }`}</span>
              </Button>
            </Control>
            <Control>
              <Button
                color="link"
                disabled={!selected.length || printDirectionsLoading}
                size="small"
                state={printDirectionsLoading ? "loading" : ""}
                onClick={handlePrintDirections}
              >
                <Icon>
                  <FontAwesomeIcon icon="print" />
                </Icon>
                <span>Print Directions</span>
              </Button>
            </Control>
          </Field>
        </TripList>
      </div>
      {OrdersSystemCodes?.data?.length > 0 &&
        Array.isArray(ORDER_DISPATCHING_COLUMNS) && (
          <ReactTable
            columns={ORDER_DISPATCHING_COLUMNS}
            data={data}
            initialSortBy={[
              { id: "StartDate", desc: false },
              { id: "Trip", desc: false }
            ]}
          />
        )}
      {/* <pre>
        <code>{JSON.stringify(data, null, 2)}</code>
      </pre> */}
    </div>
  );
};

OrderDispatchingGrid.propTypes = {
  date: PropTypes.string,
  selectedDriver: PropTypes.object
};

export default OrderDispatchingGrid;
